import { Tooltip } from 'antd';
import React, { forwardRef, useState, useMemo, useCallback } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { compact } from 'lodash';
import { FieldTypes } from 'utils/formUtils';
import message from "components/CustomMessage";
import { InfoIcon } from 'components/Icons';
import GenericMappingForm from '../GenericMappingForm';
import {Form} from 'antd';
import { uniqBy, isEmpty } from 'lodash';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';

const findingTypes = [{
  label: 'Code Smell',
  value: 'CODE_SMELL'
}, {
  label: 'Bug',
  value: 'BUG'
}, {
  label: 'Vulnerability',
  value: 'VULNERABILITY'
}, {
  label: 'Hotspot',
  value: 'HOTSPOT'
}];

const SonarqubeMappingForm = forwardRef(function SonarqubeMappingForm({disableProjectFields = false, ...props}, ref) {
  const [form] = Form.useForm();
  const featureFlags = useFeatureFlag()
  const [initialValues] = useState(() => {
    if (props.record) {
      return [];
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
        findingType: findingTypes.slice(2).map(type => type.value )
      },
    ];
  });

  const [mappedComponentList] = useState(() => {
    // this state contains the already mapped application/site
    // This is stored because the project and version dropdowns are paginated
    // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
    if (props.record) {
      const { componentName, componentDisplayName, componentNameWithKey } = props.record;
      const componentNameArray = componentName.split(',');
      const componentDisplayNameArray = componentDisplayName.split(',');
      const componentNameWithKeyArray = componentNameWithKey ? componentNameWithKey?.split(',') : featureFlags.SONARQUBE_COMPONENT_NAME_WITH_KEY ? componentDisplayNameArray.map((item, index) => `${item}/${componentNameArray[index]}`) : undefined;
      return {componentName: componentNameArray.map((id, index) => ({id, name: featureFlags.SONARQUBE_COMPONENT_NAME_WITH_KEY ? componentNameWithKeyArray[index]  : componentDisplayNameArray[index], ...( { otherProperties : { componentDisplayName: componentDisplayNameArray[index]}}) }))}
    }
    return { componentName: [] }
  })

  const [options,setOptions] = useState(mappedComponentList.componentName);

  const setOptionsFromPagination = (option) => {
    setOptions(prev => uniqBy([...prev,...option], 'id'));
  }

  const fetchComponents =  useCallback((params) => {
      const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params);
      promise.then((response) => {
        let { projects } = response;
        if(mappedComponentList["componentName"].length > 0 && !isEmpty(projects)){
          projects = [...projects, ...mappedComponentList["componentName"]];
        }
        setOptionsFromPagination(projects);
      }).catch(err =>  message.error('Problem fetching components'));
      return { promise, cancel };
  }, [props.toolName, mappedComponentList]);

  const getExtraFieldsData = useCallback((config) => {
    const { componentName, findingType } = config;
    const data = options.reduce((acc, cv) => {
      const { id, name, otherProperties: { componentDisplayName } } = cv;
      if(componentName.includes(id)){
        acc.componentName.push(id);
        if(featureFlags.SONARQUBE_COMPONENT_NAME_WITH_KEY){
          acc.componentDisplayName.push(componentDisplayName);
          acc.componentNameWithKey.push(name);
        } else {
          acc.componentDisplayName.push(name);
        }
      }
      return acc;
    },{componentName: [], componentDisplayName: [], componentNameWithKey: [] })
 
    return ({
      componentName: data.componentName.join(','),
      componentDisplayName: data.componentDisplayName.join(','),
      findingType: findingType?.toString(),
      ...(featureFlags.SONARQUBE_COMPONENT_NAME_WITH_KEY && {componentNameWithKey:  data.componentNameWithKey.join(',')}),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  },[featureFlags, options])

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    return ({
      findingType: compact(config.findingType?.split(',')),
      componentName: config.componentName.split(','),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  }, []);

  const extraFields = useMemo(() => ([
    {
      name: 'findingType',
      colSpan: '10',
      placeholder: "Leave blank to select all",
      disabled: disableProjectFields,
      type: FieldTypes.MULTI_SELECT,
      label: <>Finding Type <Tooltip title="If none is selected, all issue types will be scanned"><InfoIcon className={'m-l-xs'} /></Tooltip></>,
      options: findingTypes,
      props: {
        autoFocus: !disableProjectFields
      }
      // rules: [{ required: true, message: `Finding Type is required` }]
    }, {
      name: 'componentName',
      colSpan: '10',
      type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
      disabled: disableProjectFields,
      label: 'Sonarqube Components',
      props: {
        mode:"multiple",
        api: fetchComponents,
        apiParams: {login_id: props.loginConfigId, },
      },
      rules: [{ required: true, message: `Sonarqube Component is required` }]
    }
  ]), [props.loginConfigId, fetchComponents, disableProjectFields]);

  const handleValuesChange = (_, allData) => {
    form.setFieldsValue({ configurations: allData.configurations.map((config) => !config ? ({ findingType:["VULNERABILITY", "HOTSPOT"] }) : config)})
  }

  return (
    <GenericMappingForm
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      onValuesChange={handleValuesChange}
      form={form}
      {...props}
    />
  )
})

export default SonarqubeMappingForm;
